/**
 * Environment configuration
 * This file handles environment variables in a safe way across different environments
 */

interface Config {
  supabase: {
    url: string;
    anonKey: string;
  };
  environment: 'development' | 'staging' | 'production';
  isDevelopment: boolean;
  isProduction: boolean;
  // Development mode features
  devMode: {
    enabled: boolean;
    mockAuth: boolean;
    mockData: boolean;
    logging: boolean;
  };
}

// Helper to safely get environment variables
const getEnv = (key: string, defaultValue: string = ''): string => {
  // Try import.meta.env first (Vite)
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return (import.meta.env[key] as string) || defaultValue;
  }
  
  // Try process.env (Node/fallback)
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key] || defaultValue;
  }
  
  return defaultValue;
};

// Get mode/environment
const getMode = (): string => {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env.MODE || 'development';
  }
  if (typeof process !== 'undefined' && process.env) {
    return process.env.NODE_ENV || 'development';
  }
  return 'development';
};

const mode = getMode();

// Safely check if we're in development mode
const isDev = mode === 'development';
const isProd = mode === 'production';

// Get environment variables
const supabaseUrl = getEnv('VITE_SUPABASE_URL');
const supabaseAnonKey = getEnv('VITE_SUPABASE_ANON_KEY');
const supabaseProjectId = getEnv('VITE_SUPABASE_PROJECT_ID');

// Extract project ID from URL if not explicitly set
const extractProjectId = (url: string): string => {
  const match = url.match(/https:\/\/([^.]+)\.supabase\.co/);
  return match ? match[1] : '';
};

// Export project ID for Edge Function calls
export const projectId = supabaseProjectId || extractProjectId(supabaseUrl);
export const publicAnonKey = supabaseAnonKey;

// Determine environment
const getEnvironment = (): 'development' | 'staging' | 'production' => {
  if (isProd) return 'production';
  if (mode === 'staging') return 'staging';
  return 'development';
};

export const config: Config = {
  supabase: {
    // CRITICAL: Never hardcode credentials in production
    // Use environment variables set in deployment platform
    url: supabaseUrl,
    anonKey: supabaseAnonKey,
  },
  environment: getEnvironment(),
  isDevelopment: isDev,
  isProduction: isProd,
  // Development mode features - DISABLED in production
  devMode: {
    enabled: isDev && !isProd, // Only enabled in development, never in production
    mockAuth: isDev && !isProd, // Only use mock auth in development
    mockData: false, // Always use real Supabase data
    logging: isDev && !isProd, // Only log in development
  },
};

// Only log in development
if (isDev) {
  console.log('✅ CannaBias Config loaded:', {
    environment: mode,
    supabaseUrl: config.supabase.url ? '***' : 'MISSING',
    hasAnonKey: !!config.supabase.anonKey,
    devMode: config.devMode.enabled,
    mockAuth: config.devMode.mockAuth,
    dataSource: config.devMode.mockData ? 'Mock Data' : 'Supabase',
  });
}

// Validate configuration
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('❌ Missing Supabase credentials!');
  
  if (isProd) {
    console.error('⚠️  PRODUCTION BUILD REQUIRES ENVIRONMENT VARIABLES');
    console.error('Set these in your Vercel Dashboard → Settings → Environment Variables:');
    console.error('  - VITE_SUPABASE_URL');
    console.error('  - VITE_SUPABASE_ANON_KEY');
    
    // In production, throw error to prevent app from running without credentials
    throw new Error('Missing required environment variables for production build');
  } else {
    console.error('');
    console.error('📝 FOR LOCAL DEVELOPMENT:');
    console.error('Create a .env file in the root directory with:');
    console.error('');
    console.error('VITE_SUPABASE_URL=https://your-project.supabase.co');
    console.error('VITE_SUPABASE_ANON_KEY=your_anon_key_here');
    console.error('');
    console.error('Get these values from: https://supabase.com/dashboard');
    console.error('→ Your Project → Settings → API');
    console.error('');
    
    // In development, throw error to prevent silent failures
    throw new Error('Missing Supabase credentials - please create .env file (see console for instructions)');
  }
}